import type {
  QueueEntrySnapshot,
  TableAuthorityProjection,
  TableMutationContext,
  TableSessionSnapshot,
} from '../table/table-authority.ts';
import type {
  SmtDineInActionDescriptor,
  SmtDineInAttentionDescriptor,
  SmtDineInDispatchResult,
  SmtDineInFinancialState,
  SmtDineInIntent,
  SmtDineInMutationContext,
  SmtDineInOrderViewModel,
  SmtDineInQueueRowViewModel,
  SmtDineInRequest,
  SmtDineInSessionViewModel,
  SmtDineInTableViewModel,
  SmtDineInViewModel,
} from './smt-dine-in-contract.ts';

export interface SmtDineInTablePort {
  projection(): TableAuthorityProjection;
  session(sessionId: string): TableSessionSnapshot | null;
  openWalkInSession(
    input: {
      readonly sessionId: string;
      readonly seatingGroupId: string;
      readonly originGuestGroupId: string;
      readonly partySize: number;
      readonly tableIds: readonly string[];
    },
    context: TableMutationContext,
  ): TableSessionSnapshot;
  transferSession(sessionId: string, tableIds: readonly string[], context: TableMutationContext): TableSessionSnapshot;
  mergeSessions(
    input: { readonly primarySessionId: string; readonly secondarySessionIds: readonly string[] },
    context: TableMutationContext,
  ): TableSessionSnapshot;
  callQueueEntry(queueEntryId: string, context: TableMutationContext): QueueEntrySnapshot;
  seatQueueEntry(
    input: {
      readonly queueEntryId: string;
      readonly sessionId: string;
      readonly seatingGroupId: string;
      readonly tableIds: readonly string[];
    },
    context: TableMutationContext,
  ): TableSessionSnapshot;
  markPaidContinuing(sessionId: string, context: TableMutationContext): TableSessionSnapshot;
  payAndClose(
    sessionId: string,
    context: TableMutationContext,
  ): { readonly session: TableSessionSnapshot; readonly waitingList: readonly QueueEntrySnapshot[] };
}

export interface SmtDineInOrderProjectionInput {
  readonly orderId: string;
  readonly revision: number;
  readonly currency: string;
  readonly totalMinor: number;
}

export interface SmtDineInPaymentProjectionInput {
  readonly orderId: string;
  readonly currency: string;
  readonly version: number;
  readonly summary: { readonly outstandingMinor: number };
  readonly fullySettled: boolean;
}

export interface CreateSmtDineInSurfaceDependencies {
  readonly getTableAuthority: () => SmtDineInTablePort | null;
  readonly getOrder: (orderId: string) => SmtDineInOrderProjectionInput | null;
  readonly paymentSnapshot: (orderId: string) => SmtDineInPaymentProjectionInput;
}

interface FinancialProjection {
  readonly state: SmtDineInFinancialState;
  readonly orders: readonly SmtDineInOrderViewModel[];
  readonly currency?: string;
  readonly totalMinor?: number;
  readonly outstandingMinor?: number;
  readonly fullySettled?: boolean;
}

const requireTableAuthority = (deps: CreateSmtDineInSurfaceDependencies): SmtDineInTablePort => {
  const table = deps.getTableAuthority();
  if (!table) throw new Error('SMT_DINE_IN_TABLE_AUTHORITY_REQUIRED');
  return table;
};

const toTableContext = (context: SmtDineInMutationContext): TableMutationContext => ({
  expectedRevision: context.expectedTableRevision,
  idempotencyKey: context.idempotencyKey,
  actorId: context.actorId,
  at: context.at,
});

const financialProjection = (
  orderIds: readonly string[],
  deps: CreateSmtDineInSurfaceDependencies,
): FinancialProjection => {
  if (orderIds.length === 0) {
    return {
      state: 'empty',
      orders: [],
      totalMinor: 0,
      outstandingMinor: 0,
      fullySettled: true,
    };
  }

  const orders: SmtDineInOrderViewModel[] = [];
  let incomplete = false;
  for (const orderId of orderIds) {
    const order = deps.getOrder(orderId);
    if (!order) {
      incomplete = true;
      continue;
    }
    try {
      const payment = deps.paymentSnapshot(orderId);
      if (payment.orderId !== orderId || payment.currency !== order.currency) {
        incomplete = true;
        continue;
      }
      orders.push({
        orderId,
        orderRevision: order.revision,
        currency: order.currency,
        totalMinor: order.totalMinor,
        paymentVersion: payment.version,
        outstandingMinor: payment.summary.outstandingMinor,
        fullySettled: payment.fullySettled,
      });
    } catch {
      incomplete = true;
    }
  }

  if (incomplete || orders.length !== orderIds.length) {
    return { state: 'incomplete', orders };
  }

  const currencies = new Set(orders.map((order) => order.currency));
  if (currencies.size !== 1) {
    return { state: 'currency-mismatch', orders };
  }

  return {
    state: 'complete',
    orders,
    currency: orders[0]!.currency,
    totalMinor: orders.reduce((sum, order) => sum + order.totalMinor, 0),
    outstandingMinor: orders.reduce((sum, order) => sum + order.outstandingMinor, 0),
    fullySettled: orders.every((order) => order.fullySettled),
  };
};

const attentionFor = (
  state: SmtDineInTableViewModel['state'] | undefined,
  finance: FinancialProjection | undefined,
): SmtDineInAttentionDescriptor => {
  if (state === 'disabled') return { code: 'disabled' };
  if (state === 'partitioned') return { code: 'partitioned' };
  if (finance?.state === 'incomplete') return { code: 'financial-data-incomplete' };
  if (finance?.state === 'currency-mismatch') return { code: 'currency-mismatch' };
  if ((finance?.outstandingMinor ?? 0) > 0) return { code: 'payment-outstanding' };
  return { code: 'none' };
};

const settlementActions = (session: TableSessionSnapshot, finance: FinancialProjection): readonly SmtDineInActionDescriptor[] => {
  if (session.status === 'closed') return [];
  const canSettle = finance.state === 'empty' || (finance.state === 'complete' && finance.fullySettled === true);
  const disabledReason = finance.state === 'incomplete' || finance.state === 'currency-mismatch'
    ? 'SMT_DINE_IN_FINANCIAL_DATA_INCOMPLETE'
    : 'TABLE_SESSION_PAYMENT_OUTSTANDING';
  return [
    { actionId: 'table.transfer', enabled: true },
    { actionId: 'table.merge', enabled: true },
    {
      actionId: 'table.mark-paid-continuing',
      enabled: canSettle,
      ...(canSettle ? {} : { disabledReason }),
    },
    {
      actionId: 'table.pay-and-close',
      enabled: canSettle,
      ...(canSettle ? {} : { disabledReason }),
    },
  ];
};

const queueActions = (entry: QueueEntrySnapshot): readonly SmtDineInActionDescriptor[] => {
  if (entry.status !== 'waiting' && entry.status !== 'called') return [];
  return [
    { actionId: 'queue.call', enabled: true },
    { actionId: 'queue.seat', enabled: true },
  ];
};

export function createSmtDineInSurface(deps: CreateSmtDineInSurfaceDependencies) {
  const snapshot = (request: SmtDineInRequest): SmtDineInViewModel => {
    const table = requireTableAuthority(deps);
    const projection = table.projection();
    const sessionsById = new Map(projection.sessions.map((session) => [session.sessionId, session] as const));
    const queueById = new Map(projection.queueEntries.map((entry) => [entry.queueEntryId, entry] as const));
    const tablesById = new Map(projection.tables.map((entry) => [entry.tableId, entry] as const));
    const financeBySession = new Map<string, FinancialProjection>();

    const financeForSession = (session: TableSessionSnapshot): FinancialProjection => {
      const existing = financeBySession.get(session.sessionId);
      if (existing) return existing;
      const built = financialProjection(session.orderIds, deps);
      financeBySession.set(session.sessionId, built);
      return built;
    };

    const tables: SmtDineInTableViewModel[] = projection.tables
      .slice()
      .sort((left, right) => left.position - right.position || left.tableId.localeCompare(right.tableId))
      .map((entry) => {
        const session = entry.activeSessionId ? sessionsById.get(entry.activeSessionId) : undefined;
        const finance = session ? financeForSession(session) : undefined;
        const selectable = entry.state !== 'disabled' && entry.state !== 'partitioned';
        const disabledReason = entry.state === 'disabled'
          ? 'TABLE_DISABLED'
          : entry.state === 'partitioned'
            ? 'TABLE_PARTITIONED'
            : undefined;
        return {
          tableId: entry.tableId,
          label: entry.label,
          areaId: entry.areaId,
          position: entry.position,
          state: entry.state,
          ...(entry.activeSessionId ? { activeSessionId: entry.activeSessionId } : {}),
          ...(session ? {
            partySize: session.partySize,
            openedAt: session.openedAt,
            orderCount: session.orderIds.length,
            financialState: finance!.state,
            ...(finance!.currency ? { currency: finance!.currency } : {}),
            ...(finance!.totalMinor !== undefined ? { totalMinor: finance!.totalMinor } : {}),
            ...(finance!.outstandingMinor !== undefined ? { outstandingMinor: finance!.outstandingMinor } : {}),
            ...(finance!.fullySettled !== undefined ? { fullySettled: finance!.fullySettled } : {}),
          } : {}),
          attention: attentionFor(entry.state, finance),
          selectable,
          ...(disabledReason ? { disabledReason } : {}),
          actions: entry.state === 'available'
            ? [{ actionId: 'table.open-walk-in', enabled: true }]
            : [],
        } satisfies SmtDineInTableViewModel;
      });

    const queue: SmtDineInQueueRowViewModel[] = projection.queueEntries.map((entry) => ({
      queueEntryId: entry.queueEntryId,
      displayCode: entry.displayCode,
      partySize: entry.partySize,
      itemCount: entry.itemCount,
      status: entry.status,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
      actions: queueActions(entry),
    }));

    let selectedSession: SmtDineInSessionViewModel | undefined;
    if (request.selectedSessionId) {
      const session = sessionsById.get(request.selectedSessionId) ?? table.session(request.selectedSessionId) ?? undefined;
      if (session) {
        const finance = financeForSession(session);
        const queueEntry = session.queueEntryId ? queueById.get(session.queueEntryId) : undefined;
        selectedSession = {
          sessionId: session.sessionId,
          tableIds: [...session.tableIds],
          tableLabels: session.tableIds.map((tableId) => tablesById.get(tableId)?.label ?? tableId),
          partySize: session.partySize,
          status: session.status,
          openedAt: session.openedAt,
          updatedAt: session.updatedAt,
          ...(session.queueEntryId ? { queueEntryId: session.queueEntryId } : {}),
          ...(queueEntry ? { queueDisplayCode: queueEntry.displayCode } : {}),
          orderIds: [...session.orderIds],
          orders: finance.orders,
          financialState: finance.state,
          ...(finance.currency ? { currency: finance.currency } : {}),
          ...(finance.totalMinor !== undefined ? { totalMinor: finance.totalMinor } : {}),
          ...(finance.outstandingMinor !== undefined ? { outstandingMinor: finance.outstandingMinor } : {}),
          ...(finance.fullySettled !== undefined ? { fullySettled: finance.fullySettled } : {}),
          attention: attentionFor(undefined, finance),
          actions: settlementActions(session, finance),
        };
      }
    }

    return {
      storeId: projection.storeId,
      businessDate: projection.businessDate,
      tableRevision: projection.revision,
      tables,
      queue,
      ...(selectedSession ? { selectedSession } : {}),
    };
  };

  const dispatch = (intent: SmtDineInIntent, request: SmtDineInRequest): SmtDineInDispatchResult => {
    const table = requireTableAuthority(deps);
    const context = toTableContext(intent.context);
    switch (intent.type) {
      case 'table.open-walk-in':
        table.openWalkInSession({
          sessionId: intent.sessionId,
          seatingGroupId: intent.seatingGroupId,
          originGuestGroupId: intent.originGuestGroupId,
          partySize: intent.partySize,
          tableIds: intent.tableIds,
        }, context);
        break;
      case 'table.transfer':
        table.transferSession(intent.sessionId, intent.tableIds, context);
        break;
      case 'table.merge':
        table.mergeSessions({
          primarySessionId: intent.primarySessionId,
          secondarySessionIds: intent.secondarySessionIds,
        }, context);
        break;
      case 'queue.call':
        table.callQueueEntry(intent.queueEntryId, context);
        break;
      case 'queue.seat':
        table.seatQueueEntry({
          queueEntryId: intent.queueEntryId,
          sessionId: intent.sessionId,
          seatingGroupId: intent.seatingGroupId,
          tableIds: intent.tableIds,
        }, context);
        break;
      case 'table.mark-paid-continuing':
        table.markPaidContinuing(intent.sessionId, context);
        break;
      case 'table.pay-and-close':
        table.payAndClose(intent.sessionId, context);
        break;
    }
    return snapshot(request);
  };

  return { snapshot, dispatch };
}
